import { motion } from "framer-motion";
import { Crown, Star, Diamond, Award } from "lucide-react";
import { cn } from "@/lib/utils";
import type { ModelLevel } from "@/lib/supabase";

interface ModelLevelBadgeProps {
  level: ModelLevel;
  totalEarned: number;
  maloumTotal?: number;
  fourbasedChatTotal?: number;
  feedSalesTotal?: number;
  avatarUrl?: string | null;
  username?: string;
  className?: string;
}

const USD_TO_EUR_RATE = 0.92;

const calcModelNet = (grossChat: number, grossFeed: number) => {
  const chatterDeduction = grossChat * 0.20;
  const afterChatter = grossChat - chatterDeduction + grossFeed;
  return afterChatter * 0.40;
};

const levelConfig: Record<
  ModelLevel,
  {
    icon: typeof Crown;
    label: string;
    gradient: string;
    range: string;
  }
> = {
  bronze: {
    icon: Award,
    label: "Bronze",
    gradient: "gradient-bronze",
    range: "€0 – €10.000",
  },
  silver: {
    icon: Star,
    label: "Prata",
    gradient: "gradient-silver",
    range: "€10.000 – €50.000",
  },
  gold: {
    icon: Crown,
    label: "Dourado",
    gradient: "gradient-gold",
    range: "€50.000 – €100.000",
  },
  platinum: {
    icon: Crown,
    label: "Platina",
    gradient: "gradient-platinum",
    range: "€100.000 – €250.000",
  },
  diamond: {
    icon: Diamond,
    label: "Diamante",
    gradient: "gradient-diamond",
    range: "+€250.000",
  },
};

export function ModelLevelBadge({
  level,
  totalEarned,
  maloumTotal = 0,
  fourbasedChatTotal = 0,
  feedSalesTotal = 0,
  avatarUrl,
  username,
  className = "",
}: ModelLevelBadgeProps) {
  const config = levelConfig[level];
  const Icon = config.icon;

  // Maloum net is already in EUR
  const maloumNetEUR = calcModelNet(maloumTotal, 0);
  // 4Based net in USD, converted to EUR
  const fourbasedNetUSD = calcModelNet(fourbasedChatTotal, feedSalesTotal);
  const fourbasedNetEUR = fourbasedNetUSD * USD_TO_EUR_RATE;
  // Combined total in EUR
  const totalEUR = maloumNetEUR + fourbasedNetEUR;

  const formatEUR = (value: number) =>
    "€" + value.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      className={cn(
        "rounded-2xl p-6 text-white relative overflow-hidden",
        config.gradient,
        className
      )}
    >
      {/* Decorative Elements */}
      <div className="absolute top-0 right-0 w-32 h-32 opacity-20">
        <Icon className="w-full h-full" />
      </div>

      <div className="relative z-10">
        {/* Profile Section */}
        <div className="flex items-center gap-4 mb-6">
          <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center overflow-hidden border-2 border-white/30">
            {avatarUrl ? (
              <img
                src={avatarUrl}
                alt={username || "Avatar"}
                className="w-full h-full object-cover"
              />
            ) : (
              <Icon className="w-8 h-8" />
            )}
          </div>
          <div>
            <h2 className="font-display text-2xl tracking-wide">
              {username?.toUpperCase() || "MODELO"}
            </h2>
            <div className="flex items-center gap-2 mt-1">
              <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center">
                <Icon className="w-4 h-4" />
              </div>
              <span className="text-sm font-medium opacity-90">
                Nível {config.label}
              </span>
            </div>
          </div>
        </div>

        {/* Stats Section */}
        <div className="space-y-2 bg-white/10 rounded-xl p-4">
          <div className="flex justify-between items-center">
            <span className="text-sm opacity-80">Total Faturado (EUR)</span>
            <span className="font-display text-xl">
              {formatEUR(totalEUR)}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm opacity-80">Faixa do Nível</span>
            <span className="text-sm">{config.range}</span>
          </div>
          <p className="text-xs opacity-60 mt-1">
            Maloum (€) + 4Based convertido (taxa: 1 USD = {USD_TO_EUR_RATE}€)
          </p>
        </div>
      </div>
    </motion.div>
  );
}
